import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useNavigate } from "react-router";

import { afterBillingCompletePath } from "../account-billing";
import { useApi } from "../api-context";
import {
  resumeSubscriptionLabel,
  subscriptionPausedMemberCopy,
} from "../billing-copy";
import {
  desktopHostedCheckoutCopy,
  followHostedHttpsUrl,
} from "../hosted-https";

export function BillingResumeScreen({
  organizationId,
  billingAccountId,
  canResume,
}: {
  readonly organizationId: string;
  readonly billingAccountId: string | null;
  readonly canResume: boolean;
}) {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [hostedCopied, setHostedCopied] = useState(false);

  const resume = useMutation({
    mutationFn: async () => {
      if (billingAccountId === null) {
        throw new Error("invalid_operation");
      }
      const checkout = await api.createResubscribeCheckout(
        billingAccountId,
        organizationId,
      );
      if (checkout.billing_mode === "local" || checkout.checkout_url === null) {
        return api.completeBillingCheckout(checkout.session_id);
      }
      const follow = await followHostedHttpsUrl(checkout.checkout_url);
      setHostedCopied(follow === "copied");
      return checkout;
    },
    onSuccess: async (result) => {
      if ("kind" in result) {
        await queryClient.invalidateQueries({ queryKey: ["organizations"] });
        await queryClient.invalidateQueries({ queryKey: ["billing-usage"] });
        void navigate(afterBillingCompletePath(result));
      }
    },
  });

  return (
    <section className="page-state" aria-labelledby="billing-resume-title">
      <p className="utility-label">Billing</p>
      <h2 id="billing-resume-title">Subscription paused</h2>
      {canResume && billingAccountId !== null ? (
        <>
          <p>
            Resume the same workspace. Markdown, organization IDs, and billing
            stay put. There is no second trial.
          </p>
          <button
            className="primary-action"
            type="button"
            disabled={resume.isPending}
            onClick={() => {
              resume.mutate();
            }}
          >
            {resume.isPending ? "Opening…" : resumeSubscriptionLabel}
          </button>
        </>
      ) : (
        <p>{subscriptionPausedMemberCopy}</p>
      )}
      {hostedCopied ? <p role="status">{desktopHostedCheckoutCopy}</p> : null}
      {resume.error === null ? null : (
        <p className="form-error" role="alert">
          {resume.error.message.replaceAll("_", " ")}
        </p>
      )}
    </section>
  );
}
